import type { Catalog } from "../en";
import { settingsP3 } from "./settings-p3";
import { settingsZoho } from "./settings-zoho";

/** Filipino: Settings screen (mirrors en/settings.ts). */
export const settings: Catalog["settings"] = {
  title: "Settings",
  subtitle: "Workspace, iyong profile, developer access, seguridad at data privacy.",
  navLabel: "Mga seksyon ng Settings",
  panels: {
    org: "Organisasyon",
    profile: "Profile",
    api: "Mga API key",
    webhooks: "Mga Webhook",
    integrations: "Mga Integration",
    "secure-launch": "Secure launch",
    security: "Seguridad",
    privacy: "Data at privacy",
    audit: "Audit log",
    notifications: "Mga abiso",
    domains: "Mga domain",
    attributes: "Mga attribute",
    sla: "Mga SLA policy",
    roles: "Mga role at permission",
  },
  direction: {
    inbound: "Papasok",
    outboundEvents: "Mga papalabas na event",
    outboundCalls: "Mga papalabas na tawag",
    signIn: "Sign-in",
  },
  attributes: settingsP3.attributes,
  sla: settingsP3.sla,
  save: "I-save",
  saveChanges: "I-save ang mga pagbabago",
  discard: "Itapon",
  colActions: "Mga aksyon",
  copyFailed: "Hindi makopya. Piliin ang teksto at kopyahin nang mano-mano.",
  loadError: "Hindi ma-load ang seksyong ito.",
  loadErrorBody: "Suriin ang koneksyon at subukan muli. Walang nabago.",
  forbiddenTitle: "Wala kang access sa seksyong ito",
  forbiddenBody: "Hindi kasama sa iyong role ang workspace settings. Magagamit pa rin ang iyong profile at mga abiso.",
  planGateTitle: "Hindi kasama sa iyong plano",
  planGateBody: "Mag-upgrade ng plano para magamit ang bahaging ito ng Settings.",
  region: {
    title: "Bansa at currency ng billing",
    subtitle: "Itinatakda nito ang buwis sa invoice at ang currency ng bayad.",
    country: "Bansa ng billing",
    countryHint: "Pilipinas: 12% VAT. Ibang bansa: VAT 0% (zero-rated na export ng serbisyo).",
    currency: "Currency ng billing",
    currencyHint: "Magkakabisa ang pagbabago sa simula ng susunod na billing period, hindi sa gitna nito.",
    currencies: {
      PHP: "Piso ng Pilipinas (PHP)",
      USD: "US dollar (USD)",
    },
    tax: "Buwis sa invoice: {{label}}.",
    pending: "Lilipat sa {{currency}} sa {{date}}.",
    save: "I-save ang billing region",
    saved: "Na-save ang billing region",
    savedLater: "Na-save. Magagamit ang bagong currency mula {{date}}.",
    noAccess: "Ang may access lang sa billing ang nakakakita at nakakapagpalit ng bansa at currency ng billing.",
    loadError: "Hindi ma-load ang billing region. Subukan ulit mamaya.",
  },
  org: {
    title: "Organisasyon",
    subtitle: "Ipinapakita sa mga bisita sa widget, web app at mga email",
    name: "Pangalan ng organisasyon",
    nameRequired: "Ilagay ang pangalan ng inyong organisasyon",
    website: "Website",
    websiteInvalid: "Gumamit ng buong https:// na address",
    industry: "Industriya",
    industryNone: "Hindi pa nakatakda",
    timezone: "Timezone",
    timezones: {
      Asia_Manila: "Asia/Manila (GMT+8)",
      Asia_Singapore: "Asia/Singapore (GMT+8)",
      UTC: "UTC",
    },
    language: "Default na wika",
    languageHint: "Sumasagot pa rin ang assistant sa wika ng bisita.",
    languages: {
      en: "English",
      fil: "Filipino",
    },
    workspaceUrl: "URL ng workspace",
    workspaceUrlHint: "Makipag-ugnayan sa support para palitan ang address ng workspace.",
    address: "Address",
    city: "Lungsod",
    saved: "Na-save ang settings ng organisasyon",
    uploadLogo: "Mag-upload ng logo",
    removeLogo: "Alisin",
    logoHint: "Parisukat na PNG, JPEG o WebP, hindi bababa sa 256 × 256 px, hanggang 2 MB.",
    logoAlt: "Logo ng {{name}}",
    logoUploaded: "Na-upload ang logo",
    logoRemoved: "Inalis ang logo. Initials ang ipapakita.",
    logoUploadFailed: "Hindi ma-upload ang logo. Subukan muli.",
    logoBadType: "Gumamit ng PNG, JPEG o WebP na larawan.",
    logoTooLarge: "Mas malaki sa 2 MB ang larawang iyan.",
  },
  profile: {
    title: "Iyong profile",
    photoHint: "Ipinapakita ang iyong initials sa tabi ng iyong mga sagot at sa listahan ng team.",
    name: "Buong pangalan",
    nameRequired: "Ilagay ang iyong pangalan",
    jobTitle: "Posisyon",
    email: "Email",
    emailHint: "Kailangang i-verify muli kapag pinalitan ang email. Humingi ng tulong sa Owner ng workspace.",
    mobile: "Mobile (para sa handoff alerts)",
    mobileInvalid: "Maglagay ng tamang mobile number",
    save: "I-save ang profile",
    saved: "Na-update ang profile",
  },
  password: {
    title: "Palitan ang password",
    current: "Kasalukuyang password",
    currentRequired: "Ilagay ang kasalukuyang password",
    new: "Bagong password",
    newPlaceholder: "Hindi bababa sa {{count}} na character",
    tooShort: "Gumamit ng hindi bababa sa {{count}} na character",
    confirm: "Kumpirmahin ang bagong password",
    mismatch: "Hindi magkatugma ang mga password",
    submit: "I-update ang password",
    changed: "Napalitan ang password. Na-sign out ang ibang session.",
  },
  twoFactor: {
    title: "Two-factor authentication",
    on: "Naka-on",
    off: "Hindi pa naka-set up",
    onHint: "Authenticator app",
    offHint: "Magdagdag ng ikalawang hakbang sa pag-sign in gamit ang authenticator app.",
    setUp: "I-set up ang 2FA",
    newDevice: "Mag-set up ng bagong device",
    cancelSetup: "Kanselahin ang setup",
    step1: "Buksan ang iyong authenticator app, tulad ng Google Authenticator, Microsoft Authenticator, 1Password o Authy.",
    step2: "I-scan ang QR code na ito, o ilagay nang mano-mano ang key.",
    step3: "Ilagay ang 6-digit na code mula sa app.",
    qrLabel: "QR code para sa pag-set up ng two-factor authentication",
    keyLabel: "Setup key",
    copyKey: "Kopyahin ang key",
    sameDeviceHint: "Nagse-set up ka ba sa teleponong ito? Idagdag ang account nang diretso sa iyong authenticator app.",
    openApp: "Buksan sa authenticator app",
    preparing: "Inihahanda ang iyong setup key",
    loadErrorTitle: "Hindi namin masimulan ang setup",
    codeLabel: "6-digit na code",
    verify: "I-verify",
    enabledToast: "Naka-on na ang two-factor authentication",
    deviceVerified: "Na-verify ang bagong authenticator device",
    codesLeft_one: "Backup codes: {{count}} na lang",
    codesLeft_other: "Backup codes: {{count}} na lang",
    regenerate: "Gumawa ng bagong codes",
    regenerateTitle: "Gumawa ng bagong backup codes",
    regenerateBody: "Hindi na gagana ang mga lumang backup code. Ilagay ang code mula sa authenticator app para magpatuloy.",
    regenerated: "Nakagawa ng bagong backup codes",
    codesTitle: "I-save ang iyong backup codes",
    codesBody: "Isang beses lang gagana ang bawat code kapag nawala ang iyong telepono. Ngayon lang ito ipapakita.",
    codesDone: "Na-save ko na",
    download: "I-download ang .txt",
    disable: "I-off ang 2FA",
    disableTitle: "I-off ang two-factor authentication?",
    disableBody: "Password na lang ang poprotekta sa iyong account. Ilagay ang password at kasalukuyang code.",
    disabled: "Na-off ang two-factor authentication",
  },
  sessions: {
    title: "Mga aktibong session",
    revokeOthers: "I-sign out ang lahat ng ibang session",
    othersRevoked: "Na-sign out ang iyong ibang mga session",
    revoke: "Bawiin",
    revokeOne: "Bawiin ang session sa {{device}}",
    revoked: "Binawi ang session",
    thisDevice: "Device na ito",
    activeNow: "aktibo ngayon",
    unknownDevice: "Hindi kilalang device",
    loadError: "Hindi ma-load ang mga session.",
    emptyTitle: "Walang ibang session",
    emptyBody: "Dito ka lang naka-sign in.",
    showAll: "Ipakita ang lahat ng {{count}} session",
    showFewer: "Ipakita nang mas kaunti",
  },
  api: {
    title: "Mga API key",
    subtitle: "Gamitin ang mga ito para tawagan ang API mula sa sarili ninyong sistema. Itago sa server, huwag sa browser code.",
    directionLead: "Gamitin ang mga key na ito kapag ang iyong mga system ang tumatawag sa Sagot. Para ang Sagot ang tumawag sa iyong mga system (mga produkto, order, asset…), gamitin ang ",
    directionLink: "Mga Connector",
    directionTail: ".",
    createKey: "Gumawa ng key",
    createTitle: "Gumawa ng API key",
    name: "Pangalan",
    namePlaceholder: "hal. Customer portal",
    nameRequired: "Bigyan ng pangalan ang key",
    scopes: "Mga scope",
    scopeHelp: {
      chat_write: "Magpadala ng mensahe sa mga agent at tumanggap ng sagot",
      knowledge_write: "Nakareserba: pamahalaan ang knowledge sources (wala pang endpoint na tumatanggap nito)",
      analytics_read: "Basahin ang analytics: mga ulat ng agent at ang buod (GET /api/v1/analytics/*)",
      conversations_read: "Basahin ang mga mensahe at sagot ng staff sa mga usapang sinimulan sa API (may personal na datos)",
      workflows_read: "Basahin ang inyong mga workflow at ang mga takbo nito (bilang, layunin at resulta; walang hakbang ng workflow)",
      tickets_read: "Basahin ang mga ticket at ang kanilang pampublikong mensahe (may personal na datos)",
      tickets_write: "Magbukas ng ticket para sa inyong mga contact (kasama mula sa Pro plan)",
      organisations_read: "Basahin ang mga organisasyon at contact ng inyong mga kliyente (may personal na datos)",
      organisations_write: "Gumawa o mag-update ng mga organisasyon at contact ng kliyente gamit ang sarili ninyong ID",
      catalogue_read: "Basahin ang mga produktong ibinebenta ninyo, kasama ang presyo at stock gaya ng nakikita ng kliyente",
      orders_read: "Basahin ang mga order at ang kanilang status",
      orders_write: "Gumawa ng order para sa kliyente; sumusunod ito sa parehong kumpirmasyon at approval",
      launch_write: "Simulan ang hosted chat para sa customer na naka-sign in sa inyong site (kailangang naka-on ang Secure launch)",
      customers_read: "Hanapin ang tier, status at attributes ng customer gamit ang inyong reference (kailangang naka-on ang Secure launch)",
      customers_write: "Ipadala ang tier, status at attributes ng customer, at tapusin ang kanilang mga chat session (kailangang naka-on ang Secure launch)",
    },
    expires: "Mag-e-expire",
    expiry: {
      never: "Hindi kailanman",
      year: "Sa loob ng 1 taon",
      quarter: "Sa loob ng 90 araw",
    },
    create: "Gumawa ng key",
    secretTitle: "Kopyahin ang bagong key",
    secretLabel: "API key",
    secretWarningStrong: "Ngayon mo lang makikita ang key na ito.",
    secretWarning: "Itago ito sa environment variables ng iyong server. Kapag nawala, bawiin at gumawa ng bago.",
    saved: "Na-save ko na",
    colName: "Pangalan",
    colKey: "Key",
    colScopes: "Mga scope",
    colCreated: "Ginawa",
    colLastUsed: "Huling gamit",
    neverUsed: "Hindi pa",
    revoke: "Bawiin",
    revokeOne: "Bawiin ang {{name}}",
    revoked: "Binawi",
    expired: "Nag-expire",
    revokedToast: "Binawi ang API key. 401 na ang sagot sa mga request na gumagamit nito.",
    emptyTitle: "Wala pang API key",
    emptyBody: "Gumawa ng key para ikonekta ang inyong portal, CRM o reporting tools.",
    quickStart: "Mabilis na simula",
    sampleQuestion: "Anong oras po kayo bukas?",
    rateLimit: "Rate limit: {{count}} request/min bawat key",
    reference: "API reference",
    // Pagpapatibay ng key (M29 SEC-005): IP allowlist, in-place na pag-rotate, request signing.
    createdOn: "Ginawa noong {{date}}",
    colAllowed: "Mga pinapayagang IP",
    anyAddress: "Kahit anong address",
    moreRanges: "+{{count}} pa",
    security: "Seguridad",
    securityOne: "Mga setting ng seguridad ng {{name}}",
    rotate: "I-rotate",
    rotateOne: "I-rotate ang {{name}}",
    retires: "Titigil ang lumang secret sa loob ng {{time}}",
    signingRequiredBadge: "Kailangan ng signature",
    signingOptionalBadge: "May signing",
    createAllowlist: "Mga pinapayagang IP address",
    createAllowlistHint: "Opsyonal. Iwanang walang laman para tanggapin ang tawag mula sa kahit anong address.",
    remaining: {
      hm: "{{hours}} oras {{minutes}} min",
      m: "{{minutes}} min",
      soon: "wala pang isang minuto",
      over: "ngayon",
    },
    allowlist: {
      title: "IP allowlist at signing",
      description: "Mga patakaran para sa {{name}}. Ang pagbabago ay para na sa susunod na tawag.",
      sectionIps: "Mga pinapayagang IP address",
      hint: "Ang tawag mula sa ibang address ay makakakuha ng 403 ip_not_allowed. Iwanang walang laman ang listahan para tanggapin ang kahit anong address.",
      note: "Binabasa ng Sagot ang address ng tumatawag mula sa mga pinagkakatiwalaang hop ng iyong proxy chain lamang, kaya hindi nakakatulong ang peke na X-Forwarded-For header.",
      add: "Idagdag",
      placeholder: "203.0.113.0/24 o 2001:db8::/32",
      empty: "Walang limitasyon: kahit anong address ay puwedeng gumamit ng key na ito.",
      count: "{{count}} sa {{max}}",
      remove: "Alisin ang {{item}}",
      duplicate: "Nasa listahan na ang {{item}}",
      full: "Hanggang {{max}} address o range lamang.",
      added: "Naidagdag ang {{item}}",
      removed: "Naalis ang {{item}}",
      invalid: "Maglagay ng IPv4 o IPv6 address o range, tulad ng 203.0.113.0/24.",
      save: "I-save ang allowlist",
      saved: "Na-save ang allowlist.",
    },
    signing: {
      title: "Request signing",
      off: "Naka-off",
      optional: "Naka-on, opsyonal",
      required: "Kailangan",
      body: "I-sign ang bawat tawag gamit ang HMAC para hindi sapat ang nanakaw na key lamang. Ipadala ang {{header}}: t=…,v1=… para sa method, path at body; tinatanggihan ang request na mas matanda sa 5 minuto o ipinadala nang dalawang beses.",
      docs: "Paano mag-sign ng mga request",
      require: "Tanggihan ang mga request na hindi naka-sign",
      turnOn: "I-on ang signing",
      reissue: "Gumawa ng bagong secret",
      turnOff: "I-off ang signing",
      offToast: "Naka-off na ang request signing.",
      issuedTitle: "Kopyahin ang iyong signing secret",
      secretLabel: "Signing secret",
      warningStrong: "Ito lang ang pagkakataong makikita ninyo ang secret na ito.",
      warning: "Itago ito katabi ng API key sa inyong server. Papalitan agad nito ang kasalukuyan kapag gumawa ng bago.",
      saved: "Na-save ko na",
    },
    rotateDialog: {
      title: "I-rotate ang {{name}}",
      intro: "Gagawa ng bagong secret para sa key na ito. Gagana pa ang kasalukuyang secret nang {{hours}} oras para maipalit ninyo ang bago nang walang downtime, pagkatapos ay tatanggihan na ito.",
      again: "May rotation na tumatakbo. Tatapusin agad ng muling pag-rotate ang naunang secret.",
      confirm: "I-rotate ang key",
      doneTitle: "Kopyahin ang iyong bagong key",
      secretLabel: "Bagong API key",
      warningStrong: "Ito lang ang pagkakataong makikita ninyo ang key na ito.",
      warning: "Ilagay ito sa inyong server. Gagana pa ang lumang secret hanggang matapos ang countdown.",
      countdown: "Titigil ang lumang secret sa loob ng",
      saved: "Na-save ko na",
    },
  },
  webhooks: {
    title: "Mga endpoint",
    subtitle: "Nagpapadala kami ng JSON sa mga URL na ito kapag may nangyaring event",
    add: "Magdagdag ng endpoint",
    addTitle: "Magdagdag ng webhook endpoint",
    editTitle: "I-edit ang webhook endpoint",
    url: "URL ng endpoint",
    urlPlaceholder: "https://your-server.example.com/webhooks",
    urlInvalid: "Gumamit ng pampublikong https:// URL",
    events: "Mga event",
    format: "Format ng mensahe",
    formats: {
      sagot: "Sagot JSON",
      discord: "Discord",
      slack: "Slack",
    },
    formatHint: {
      sagot: "Naka-sign na JSON event, para sa sarili mong server.",
      discord: "Nababasang mensahe para sa webhook ng isang Discord channel.",
      slack: "Nababasang mensahe para sa incoming webhook ng Slack.",
    },
    formatSuggest: "Mukhang webhook URL ng {{format}} ito.",
    formatUse: "Gamitin ang format na {{format}}",
    delivery: {
      label: "Kailan magpapadala",
      modes: { events: "Mga naka-subscribe na event", filtered: "Piling event", workflows: "Mula lang sa mga workflow ko" },
      sentBy: "Ipinapadala ng workflow na Developer webhooks.",
      sentByLink: "Buksan ang workflow",
      paused: {
        title: "Naka-pause ang pagpapadala ng event",
        body: "Naka-pause ang workflow na Developer webhooks, kaya walang event na ipinapadala sa anumang endpoint. Gumagana pa rin ang mga test send at ang Send webhook step ng sarili ninyong workflow.",
        action: "Buksan ang workflow para ituloy ito",
        badge: "Naka-pause ang mga event",
      },
      hints: {
        events: "Bawat event ng napiling uri, mula sa lahat ng agent at channel.",
        filtered: "Ang mga napiling event, mula lang sa mga channel at nagpadala na pipiliin mo.",
        workflows: "Walang awtomatikong ipinapadala. Ang Send webhook o Data connector step ng isang workflow ang magpapasya kung kailan at ano.",
      },
      eventsRequired: "Pumili ng kahit isang event, o magpadala lang mula sa workflows.",
      connectorAuthHint: "Available ang connector authentication (isang key na ipinapadala ng Data connector ng workflow) sa mga endpoint na nakatakda sa Mula lang sa mga workflow ko.",
      channels: "Sa mga channel na ito lang",
      roles: "Mga mensahe lang mula sa",
      roleNames: { visitor: "Mga bisita", assistant: "Ang assistant", staff: "Staff" },
      filtersHint: "Iwanang walang laman ang listahan para walang limitasyon. Hindi kailanman ipinapadala ang mga awtomatikong linya.",
    },
    formatMismatch: {
      discord: "Kailangan ng Discord webhook URL ang format na Discord (https://discord.com/api/webhooks/…).",
      slack: "Kailangan ng Slack incoming webhook URL ang format na Slack (https://hooks.slack.com/services/…).",
    },
    enabled: "Naka-on ang endpoint",
    save: "I-save ang endpoint",
    saved: "Na-save ang webhook endpoint",
    deleted: "Binura ang webhook endpoint",
    deleteOne: "Burahin ang endpoint {{url}}",
    edit: "I-edit",
    editOne: "I-edit ang endpoint {{url}}",
    healthy: "Maayos",
    failures_one: "{{count}} pagkabigo (24h)",
    failures_other: "{{count}} pagkabigo (24h)",
    disabled: "Naka-off",
    sendTest: "Magpadala ng test",
    testOk: "Naipadala ang test event → {{code}} sa {{ms}} ms",
    testFailed: "Nabigo ang test event → {{code}} pagkatapos ng {{ms}} ms",
    testReceiver: "Sagot ng tumanggap: {{excerpt}}",
    rotate: "Palitan ang secret",
    rotateTitle: "Palitan ang signing secret",
    rotateBody: "Gagawa ng bagong secret para sa endpoint na ito. Gagana pa ang luma sa loob ng 24 oras para ma-update ang inyong server.",
    rotated: "Napalitan ang signing secret. Gagana pa ang luma sa loob ng 24 oras.",
    secretTitle: "Kopyahin ang signing secret",
    secretLabel: "Signing secret",
    secretWarning: "Ngayon mo lang makikita ang secret na ito. Itago sa server para ma-verify ang bawat request.",
    signingTitle: "Mga signing secret",
    signingBodyLead: "May",
    signingBodyTail: "header (HMAC-SHA256 ng body) ang bawat request, gawa sa sariling secret ng endpoint. I-verify ito bago pagkatiwalaan ang payload. Isang beses lang ipinapakita ang secret kapag ginawa o pinalitan.",
    connectorRequest: "Request ng data connector",
    connectorAuth: {
      title: "Connector authentication",
      notSet: "Hindi pa naka-set",
      hint: "Isang header na ipinapadala ng Data connector sa bawat request sa endpoint na ito, gaya ng Authorization o X-Api-Key. Naka-encrypt ang secret at hindi na ito ipapakita ulit: puwede itong palitan o alisin.",
      set: "I-set ang authentication",
      replace: "Palitan",
      remove: "Alisin",
      setTitle: "I-set ang connector authentication",
      replaceTitle: "Palitan ang connector authentication",
      dialogBody: "Ipinapadala ang halaga sa header na ito sa bawat connector request. Huling apat na karakter lang ang itinatabi namin para ipakita.",
      header: "Pangalan ng header",
      headerHint: "Halimbawa, Authorization o X-Api-Key.",
      headerPlaceholder: "Authorization",
      headerProblems: { empty: "Ilagay ang pangalan ng header.", invalid: "Letra, numero at - lang ang gamitin (hanggang 40 karakter).", forbidden: "Itinatakda ng Sagot o ng koneksyon ang header na ito kaya hindi ito magagamit." },
      value: "Secret na halaga",
      valueHint: "I-paste ang buong halaga, gaya ng Bearer abc123. Hindi na ito mababasa ulit.",
      valueProblems: { empty: "Ilagay ang secret na halaga.", long: "Masyadong mahaba ang halaga (hanggang {{max}} karakter).", control: "Hindi puwede ang line break o control character sa halaga." },
      save: "I-save ang authentication",
      saved: "Na-save ang connector authentication",
      removed: "Inalis ang connector authentication",
      removeTitle: "Alisin ang connector authentication?",
      removeBody: "Ipapadala nang walang header ang mga request ng Data connector sa endpoint na ito hanggang i-set ninyo ulit. Hindi na mababawi ang naka-save na halaga.",
    },
    deliveries: "Mga kamakailang delivery",
    deliveriesError: "Hindi ma-load ang mga delivery.",
    endpointFilter: "Endpoint",
    retryFailed: "Ulitin ang mga nabigo",
    redeliver: "Ipadala muli",
    redeliverOne: "Ipadala muli ang {{event}}",
    redelivering_one: "Inuulit ang {{count}} delivery…",
    redelivering_other: "Inuulit ang {{count}} delivery…",
    retryOf: "ulit {{attempt}}/{{max}}",
    receiverSaid: "Sagot ng tumanggap",
    nextRetry: "Susunod na ulit: {{time}}.",
    notRetried: "Hindi na uulitin. Ayusin ang endpoint, saka ipadala muli.",
    ms: "{{value}} ms",
    colEvent: "Event",
    colEndpoint: "Endpoint",
    colStatus: "Status",
    colDuration: "Tagal",
    colTime: "Oras",
    status: {
      pending: "Naghihintay",
      succeeded: "Naipadala",
      failed: "Nabigo",
      retrying: "Inuulit",
    },
    noDeliveriesTitle: "Wala pang delivery",
    noDeliveriesBody: "Magpadala ng test event para masuri kung sumasagot ang endpoint.",
    emptyTitle: "Walang webhook endpoint",
    emptyBody: "Magdagdag ng endpoint para matanggap agad sa inyong sistema ang mga usapan, handoff at lead.",
  },
  integrations: {
    connect: "Ikonekta",
    disconnect: "Idiskonekta",
    connectTitle: "Ikonekta ang {{name}}",
    connected: "Nakakonekta na ang {{name}}",
    disconnected: "Nadiskonekta ang {{name}}",
    statusConnected: "Nakakonekta",
    statusDisconnected: "Hindi nakakonekta",
    statusUnavailable: "Hindi pa available",
    unavailableHint: "Hindi pa available ang integration na ito. I-e-enable ito ng CRUD.IT para sa inyong workspace.",
    fields: {
      spreadsheet_id: "Spreadsheet ID",
      sheet_name: "Pangalan ng sheet",
      portal_id: "Portal ID",
      pipeline_id: "Pipeline ID",
      calendar_id: "Calendar ID",
      default_duration_min: "Default na haba ng booking (minuto)",
    },
    zoho: settingsZoho,
    google: {
      name: "Google Calendar",
      description: "Hinahayaan ang assistant na mag-alok ng totoong bakanteng oras at magdagdag ng kumpirmadong booking sa iyong calendar.",
      connect: "Ikonekta ang Google Calendar",
      redirecting: "Nire-redirect sa Google…",
      connectNote: "Magsa-sign in ka sa Google at papayagan mo ang {{product}} na basahin ang free/busy na oras at magdagdag ng event. Puwede mo itong idiskonekta anumang oras.",
      unavailable: "Hilingin sa platform team na i-set up ang Google sign-in.",
      notConfigured: "Hindi pa naka-set up ang Google sign-in para sa platform na ito. Makipag-ugnayan sa platform team.",
      connectedAs: "Nakakonekta bilang {{email}}",
      calendar: "Calendar para sa booking",
      calendarHint: "Dito idinaragdag ang mga kumpirmadong booking, at hinaharangan ng mga abalang oras dito ang mga slot.",
      calendarLoading: "Nilo-load ang iyong mga calendar",
      calendarError: "Hindi namin ma-load ang iyong mga calendar mula sa Google.",
      calendarEmpty: "Walang calendar sa Google account na ito na mapagdaragdagan ng event.",
      primary: "{{name}} (pangunahin)",
      meet: "Magdagdag ng Google Meet link sa mga bagong booking",
      meetHint: "Magkakaroon ng video link ang mga booking sa calendar. Puwede rin itong i-on para sa isang booking action lang.",
      saveCalendar: "I-save ang calendar",
      calendarSaved: "Na-update ang calendar para sa booking",
      disconnectTitle: "Idiskonekta ang Google Calendar?",
      disconnectBody: "Titigil ang {{product}} sa pagbasa ng iyong calendar at paggawa ng event. Maghihintay ng kumpirmasyon ng team mo ang mga booking na hihilingin ng bisita pagkatapos nito. Mananatili sa calendar mo ang mga event na naroon na.",
      disconnectConfirm: "Idiskonekta ang Google Calendar",
      disconnected: "Nadiskonekta ang Google Calendar",
      callback: {
        docTitle: "Kinokonekta ang Google Calendar",
        title: "Kinokonekta ang Google Calendar",
        loading: "Tinatapos ang koneksyon sa Google…",
        connected: "Nakakonekta na ang Google Calendar. Piliin kung saan mapupunta ang mga booking.",
        failedTitle: "Hindi namin natapos ikonekta ang Google Calendar",
        failedBody: "Magsimulang muli sa Settings → Integrations.",
        deniedTitle: "Kinansela ang Google sign-in",
        deniedBody: "Walang naikonekta. Puwede mong subukang muli sa Settings → Integrations.",
        missingTitle: "Nag-expire na ang sign-in link na ito",
        missingBody: "Magsimulang muli sa Settings → Integrations para maitugma namin ito sa iyong workspace.",
        back: "Bumalik sa Integrations",
        backHome: "Bumalik sa console",
      },
    },
    sheets: {
      name: "Google Sheets",
      description: "Idinadagdag ang bawat bagong lead bilang row sa isang Google Sheet na mabubuksan ng iyong team.",
      connect: "Ikonekta ang Google Sheets",
      redirecting: "Lilipat sa Google…",
      connectNote: "Mag-sign in ka sa Google. Gagawa ang {{product}} ng bagong spreadsheet para sa iyong mga lead at ang mga file lang na ginawa nito ang mabubuksan nito, hindi ang iba pang laman ng iyong Drive.",
      unavailable: "Hilingin sa platform team na i-set up ang Google sign-in.",
      notConfigured: "Hindi pa naka-set up ang Google sign-in sa platform na ito. Kausapin ang platform team.",
      connectedTo: "Napupunta ang mga bagong lead sa “{{title}}”.",
      open: "Buksan ang spreadsheet",
      disconnectTitle: "Idiskonekta ang Google Sheets?",
      disconnectBody: "Hihinto ang {{product}} sa pagdagdag ng mga bagong lead sa spreadsheet. Mananatili sa iyong Google Drive ang spreadsheet at ang mga lead na naroon na.",
      disconnectConfirm: "Idiskonekta ang Google Sheets",
      disconnected: "Nadiskonekta ang Google Sheets",
      callback: {
        docTitle: "Kinokonekta ang Google Sheets",
        title: "Kinokonekta ang Google Sheets",
        loading: "Ginagawa ang spreadsheet ng iyong mga lead…",
        connected: "Nakakonekta na ang Google Sheets. Idadagdag ang mga bagong lead sa iyong spreadsheet.",
        failedTitle: "Hindi namin natapos ikonekta ang Google Sheets",
        failedBody: "Magsimulang muli sa Settings → Integrations.",
        deniedTitle: "Kinansela ang Google sign-in",
        deniedBody: "Walang naikonekta. Puwede mong subukang muli sa Settings → Integrations.",
        missingTitle: "Nag-expire na ang sign-in link na ito",
        missingBody: "Magsimulang muli sa Settings → Integrations para maitugma namin ito sa iyong workspace.",
        back: "Bumalik sa Integrations",
        backHome: "Bumalik sa console",
      },
    },
  },
  security: {
    title: "Mga patakaran sa pag-sign in",
    require2fa: "Kailanganin ang 2FA sa lahat ng miyembro",
    membersWithout2fa_one: "{{count}} miyembro ang wala pang 2FA at hihingan sa susunod na pag-sign in.",
    membersWithout2fa_other: "{{count}} miyembro ang wala pang 2FA at hihingan sa susunod na pag-sign in.",
    allHave2fa: "Lahat ng miyembro ay may 2FA na.",
    domains: "Mga pinapayagang email domain",
    domainsHint: "Ang mga domain lang na ito ang maiimbitahan bilang miyembro. Iwanang blangko para payagan ang lahat.",
    domainsPlaceholder: "example.ph",
    domainsAdd: "Idagdag",
    domainsRemove: "Alisin ang {{domain}}",
    domainsEmpty: "Maaaring imbitahan ang anumang email domain",
    domainsCount: "{{count}} sa {{max}}",
    domainsInvalid: "Maglagay ng domain gaya ng example.ph, nang walang espasyo, http:// o path.",
    domainsDuplicate: "Nasa listahan na ang {{domain}}.",
    domainsFull: "Hanggang {{max}} domain lang ang maidaragdag. Mag-alis ng isa para makapagdagdag.",
    domainsAdded: "Naidagdag ang {{domain}}",
    domainsRemoved: "Naalis ang {{domain}}",
    timeout: "Session timeout",
    timeouts: {
      "30m": "30 minutong walang galaw",
      "8h": "8 oras (isang araw ng trabaho)",
      "7d": "7 araw",
      "30d": "30 araw",
    },
    ipAllowlist: "IP allowlist",
    ipPlaceholder: "Isang IP o CIDR bawat linya",
    ipHint: "Iwanang blangko para payagan ang pag-sign in kahit saan (hanggang {{count}} entry).",
    ipInvalid: "Suriin ang mga entry na ito: {{items}}",
    saved: "Na-save ang security settings",
    samlTitle: "SAML single sign-on",
    proPlan: "Pro plan",
    samlBody: "Paparating na ang single sign-on para sa mga miyembro ng team. Ang pag-sign in ng end user ay nasa",
    samlLink: "Audiences → Identity providers",
    samlToggle: "I-enable ang SAML",
    sso: {
      title: "Single sign-on",
      body: "Hayaang mag-sign in ang iyong team gamit ang identity provider ng kumpanya, sa SAML 2.0 o OpenID Connect.",
      lockedTitle: "Kasama ang single sign-on sa Pro",
      lockedBody: "Mag-upgrade sa Pro para ikonekta ang SAML o OpenID Connect at hingin ang sign-in ng kumpanya para sa iyong mga domain.",
      upgrade: "Tingnan ang mga plan",
      emptyTitle: "Walang nakakonektang identity provider",
      emptyBody: "Ikonekta ang Okta, Microsoft Entra ID, Google Workspace o anumang SAML 2.0 o OpenID Connect provider. Nananatili ang password ng mga miyembro hanggang i-on mo ang enforcement.",
      connect: "Ikonekta ang identity provider",
      protocol: "Protocol",
      saml: "SAML 2.0",
      oidc: "OpenID Connect",
      providerSection: "Identity provider",
      spTitle: "Ibigay ito sa iyong identity provider",
      spEntityId: "Entity ID ng service provider",
      spAcs: "Assertion consumer service URL",
      spMetadata: "Metadata URL ng service provider",
      spRedirect: "Redirect URI",
      copy: "Kopyahin",
      copied: "Nakopya",
      metadata: "Metadata ng identity provider",
      metadataHint: "I-upload ang metadata XML file na ibinigay ng iyong identity provider.",
      metadataUpload: "Mag-upload ng metadata file",
      metadataLoaded: "Na-load ang {{name}}",
      metadataTooLarge: "Mas malaki sa 256 KB ang file na iyon.",
      issuer: "Issuer",
      issuerHint: "Halimbawa https://login.example.com. Binabasa namin ang iba pang address mula sa discovery document nito.",
      clientId: "Client ID",
      clientSecret: "Client secret",
      clientSecretSaved: "May naka-save na secret. Iwanang walang laman para panatilihin ito.",
      domains: "Mga e-mail domain ng kumpanya",
      domainsHint: "Ang mga address lang sa mga domain na ito ang mag-sa-sign in sa iyong provider. Hanggang {{count}}.",
      domainAdd: "Magdagdag ng domain",
      domainPlaceholder: "company.com",
      domainInvalid: "Maglagay ng domain tulad ng company.com.",
      certificates: "Mga signing certificate",
      certExpires: "mapapaso sa {{date}}",
      save: "I-save ang koneksyon",
      saved: "Na-save ang single sign-on",
      remove: "Alisin ang koneksyon",
      removed: "Naalis ang single sign-on",
      removeTitle: "Alisin ang single sign-on?",
      removeBody: "Magsa-sign in muli ang mga miyembro gamit ang password at mag-o-off ang enforcement.",
      test: "Subukan ang sign-in",
      testHint: "Mag-sign in nang isang beses sa iyong provider gamit ang sarili mong account. Kailangan ng pumasang test bago mo hingin ang single sign-on.",
      testOk: "Pumasa ang test sign-in.",
      testFailed: "Hindi pumasa ang test sign-in: {{reason}} Suriin ang mga setting at subukan muli.",
      tested: "Huling sinubukan noong {{date}}",
      neverTested: "Hindi pa nasusubukan",
      enforceTitle: "Hingin ang single sign-on",
      enforceBody: "Naka-off ang pag-sign in gamit ang password para sa mga domain sa itaas, maliban sa break-glass owner.",
      enforceNeedsTest: "Magpatakbo muna ng pumasang test sign-in.",
      breakGlass: "Break-glass owner",
      breakGlassHint: "Pinapanatili ng owner na ito ang pag-sign in gamit ang password kung hindi available ang iyong provider.",
      unsaved: "I-save ang koneksyon para makapagpatakbo ng test.",
      loadError: "Hindi ma-load ang single sign-on.",
      reasons: {
        failed: "hindi tinanggap ang sagot ng provider.",
        unavailable: "hindi available ang single sign-on sa workspace na ito.",
        not_a_member: "hindi miyembro ng workspace na ito ang account na iyon.",
        domain: "hindi nakalista ang e-mail domain na iyon.",
        expired: "natagalan ang pag-sign in. Magsimulang muli.",
      },
    },
  },
  privacy: {
    noTrainingTitle: "Hindi gagamitin ang inyong datos sa pag-train ng model — garantisado",
    noTrainingBody: "Hindi kailanman gagamitin ang inyong mga dokumento at usapan para mag-train ng aming model o ng anumang AI provider. Zero-retention API settings ang gamit namin sa mga model provider. Nakasulat ito sa inyong Data Processing Agreement.",
    retentionTitle: "Retention",
    keepConversations: "Itago ang mga usapan nang",
    keepHint: "Hindi apektado ang mga lead na na-export na sa CRM.",
    keepAttachments: "Itago ang mga na-upload na attachment nang",
    days_one: "{{count}} araw",
    days_other: "{{count}} araw",
    piiTitle: "Default na PII redaction",
    piiBody: "Tinatakpan bago i-store ang mensahe o ipakita sa staff. Nakikita pa rin ng AI ang teksto para makasagot.",
    pii: {
      mobile: "Mga mobile number",
      email: "Mga email address",
      national_id: "National ID at member number",
      card_bank: "Numero ng card at bank account",
      address: "Address ng tirahan",
      gov_id: "Government ID (PhilSys, TIN, SSS)",
    },
    saved: "Na-save ang privacy settings",
    complianceTitle: "Mga dokumento sa compliance",
    complianceBody: "Ang inyong organisasyon ang personal information controller; ang CRUD.IT Solutions Inc. ang processor sa ilalim ng Data Privacy Act of 2012 (RA 10173).",
    dpa: "Data Processing Agreement",
    subprocessors: "Listahan ng mga subprocessor",
    npcNote: "Huwag kalimutang ilista ang serbisyong ito sa inyong NPC registration at privacy notice, at pangalanan ang inyong Data Protection Officer.",
    exportTitle: "I-export ang lahat ng datos",
    exportBody: "I-download ang bawat dokumento, usapan, lead at setting bilang ZIP (JSON + orihinal na file). Kapaki-pakinabang sa data subject access request at audit.",
    exportAction: "I-export ang lahat ng datos",
    exportStarted: "Nagsimula ang export. Mag-e-email kami sa {{email}} kapag handa na ang ZIP.",
    exportRunning: "Inihahanda ang export…",
    exportFailed: "Nabigo ang huling export",
    exportDownload: "I-download ang export ({{date}})",
    exportExpired: "Nag-expire na ang huling export. Magsimula ng bago.",
    dangerTitle: "Mapanganib na bahagi",
    deleteHeading: "Burahin ang workspace na ito",
    deleteSummary: "Permanenteng buburahin ang lahat ng agent, knowledge source, usapan at lead pagkatapos ng 14 na araw. Hindi na ito maibabalik.",
    deleteAction: "Burahin ang workspace",
    deleteTitle: "Burahin ang {{workspace}}?",
    deleteBody: "Titigil agad sa pagsagot ang lahat ng agent. Permanenteng buburahin ang datos pagkatapos ng 14 na araw. Puwedeng kanselahin ng Owner sa panahong iyon.",
    deleteConfirm: "I-type ang {{workspace}} para kumpirmahin",
    deleteScheduled: "Nakatakdang burahin ang {{workspace}} sa {{date}}. Puwedeng kanselahin bago noon.",
    deletePending: "Nakatakdang burahin sa {{date}}",
    deletePendingBody: "Tumigil na sa pagsagot ang mga agent. Kanselahin para maibalik ang workspace.",
    deleteCancel: "Kanselahin ang pagbura",
    deleteCancelled: "Kinansela ang pagbura. Aktibo na muli ang workspace.",
  },
  audit: {
    title: "Audit log",
    subtitle: "Bawat pagbabago sa workspace na ito · itinatago nang 1 taon",
    filter: "I-filter ayon sa tao, aksyon o target…",
    since: "Mula petsa",
    until: "Hanggang petsa",
    export: "I-export ang CSV",
    colTime: "Oras",
    colActor: "Gumawa",
    colAction: "Aksyon",
    colTarget: "Target",
    colIp: "IP address",
    system: "Sistema",
    viaSupport: "(sa pamamagitan ng CRUD.IT support)",
    filteredTo: "Naka-filter kay {{name}}",
    oneMember: "isang miyembro",
    clearActor: "Alisin ang filter ng miyembro",
    loadMore: "Mag-load pa",
    emptyTitle: "Walang tugmang aktibidad",
    emptyBody: "Subukan ang ibang paghahanap o petsa.",
  },
  notifications: {
    title: "Mga abiso",
    subtitle: "Piliin kung ano ang gusto mong malaman at saan. Para sa iyo sa workspace na ito.",
    colType: "Abiso",
    colOn: "On",
    channels: {
      in_app: "Sa app",
      email: "Email",
    },
    enableOne: "Mga abiso sa {{type}}",
    channelOne: "{{type}} sa {{channel}}",
    required: "Laging naka-on",
    saved: "Na-save ang mga kagustuhan sa abiso",
    types: {
      handoff_requested: "Mga handoff request",
      source_processed: "Handa na ang source",
      source_failed: "Nabigo ang source",
      usage_threshold: "Mga alerto sa paggamit",
      payment_failed: "Problema sa bayad",
      weekly_report: "Lingguhang ulat",
      knowledge_gap: "Mga kakulangan sa kaalaman",
      eval_regression: "Pagbaba sa ebalwasyon",
      lead_assigned: "Lead na itinalaga sa iyo",
      new_lead: "Mga bagong lead",
      new_booking: "Mga bagong booking",
      workflow: "Mga alerto ng workflow",
      security: "Mga alerto sa seguridad",
      team: "Mga pagbabago sa team",
    },
    hints: {
      handoff_requested: "May bisitang humingi ng tao.",
      source_processed: "Tapos nang i-proseso ang isang dokumento.",
      source_failed: "Hindi ma-proseso ang isang dokumento.",
      usage_threshold: "Malapit na sa limit ng plano ang mensahe o storage.",
      payment_failed: "Hindi pumasok ang isang singil.",
      weekly_report: "Buod ng mga usapan, resolution at kakulangan tuwing Lunes.",
      knowledge_gap: "Paulit-ulit na tinatanong ang hindi masagot ng assistant.",
      eval_regression: "Mas mababa ang score ng isang evaluation run kaysa dati.",
      lead_assigned: "May bago o inilipat na lead na kailangang i-follow up.",
      new_lead: "May nagbahagi ng contact details sa assistant.",
      new_booking: "May bisitang nag-book ng oras, o humiling ng oras na kailangan ng iyong kumpirmasyon.",
      workflow: "Inabisuhan ka ng isang workflow na inayos ninyo.",
      security: "Mga bagong sign-in, 2FA at pagpapalit ng password.",
      team: "Mga tinanggap na imbitasyon at pagbabago ng role.",
    },
  },
  domains: {
    title: "Custom domain",
    subtitle: "Ipakita ang web app sa sarili ninyong address, hal. ask.yourdomain.com",
    hostname: "Domain",
    hostnameHint: "Gumamit ng subdomain tulad ng ask.yourdomain.com. Magdadagdag kayo ng DNS records pagkatapos.",
    placeholder: "ask.example.com",
    invalid: "Maglagay ng subdomain gaya ng ask.example.com",
    agent: "Agent",
    agentHint: "Itali ang domain sa isang agent, o ipakita ang lahat ng pampublikong agent.",
    allAgents: "Lahat ng pampublikong agent",
    add: "Magdagdag ng domain",
    added: "Naidagdag ang {{host}}. Idagdag ang DNS records sa ibaba, saka i-verify.",
    recordsHint: "Karaniwang abot ng ilang minuto ang pagbabago at maaaring umabot ng isang oras. Sinusuri namin kada oras; gamitin ang I-verify para suriin agad.",
    recordsTitle_one: "Idagdag ang record na ito sa inyong DNS provider",
    recordsTitle_other: "Idagdag ang mga record na ito sa inyong DNS provider",
    noRecords: "Walang kailangang baguhin sa DNS ngayon. Hinihintay na lang namin ang certificate.",
    colProxy: "Proxy",
    colStatus: "Katayuan",
    copyName: "Kopyahin ang pangalan ng {{type}} record",
    copyValue: "Kopyahin ang halaga ng {{type}} record",
    proxyDnsOnly: "DNS only",
    proxyNone: "Hindi naaangkop",
    recordFound: "Nakita",
    recordMissing: "Hindi pa nakikita",
    tip: {
      cloudflareTitle: "Nasa Cloudflare ang inyong DNS.",
      cloudflareBody: "Itakda ang proxy status ng record sa DNS only (grey cloud). Kapag naka-on ang orange cloud, hindi maibibigay ang certificate. Kung nakaturo na ang {{host}} sa proxy ng Cloudflare, kailangang baguhin ang kasalukuyang record nito, o ang * record.",
    },
    progress: {
      label: "Progreso ng set-up para sa {{host}}",
      added: "Naidagdag ang domain",
      dns: "Nakita ang DNS record",
      certificate: "Naibigay ang certificate",
      live: "Live na",
      state: { done: "tapos na", pending: "naghihintay", failed: "nabigo" },
      reached: "Mula {{when}}",
      checked: "Hindi pa. Sinuri {{when}}",
      notChecked: "Hindi pa nasusuri",
      failedHint: "Hindi na kilala ng hosting provider ang domain na ito. Alisin ito at idagdag muli.",
    },
    root: {
      title: "Bumubukas ang chat sa mismong address",
      on: "Naka-on",
      body: "Bumubukas ang chat sa {{url}}. Hindi mapupunta ang mga bisita sa sign-in ng console.",
      open: "Buksan",
      openLabel: "Buksan ang chat sa {{host}}",
    },
    branding: {
      title: "Itago ang “Powered by {{product}}”",
      body: "Tinatanggal ang footer sa hosted chat at sign-in page. Baguhin ito sa Widget Studio, sa Appearance.",
      lockedBody: "Available sa Pro at Institution plan. Mag-upgrade para matanggal ang footer.",
      shown: "Nakikita",
      hidden: "Nakatago",
      locked: "Naka-lock",
      open: "Buksan ang Widget Studio",
      upgrade: "Mag-upgrade",
    },
    botCheck: {
      title: "Bot check sa address na ito",
      added: "Pinapayagan",
      manual: "Isang manual na hakbang",
      addedBody: "Nasa listahan ng pinapayagang hostname ng bot check ang {{host}}, kaya makakapag-sign in at makakapag-chat ang mga bisita rito.",
      manualBody: "Hindi namin awtomatikong naidagdag ang {{host}} sa pinapayagang hostname ng bot check. Hilingin sa Sagot support na idagdag ito, saka gamitin ang I-verify para suriin muli. Hanggang doon, maaaring magpakita ng verification error ang chat sa address na ito.",
    },
    recordsLabel: "Mga DNS record para sa {{host}}",
    colType: "Uri",
    colName: "Pangalan",
    colValue: "Halaga",
    verify: "I-verify",
    verified: "Na-verify ang {{host}}",
    notYet: "Hindi pa namin makita ang DNS records. Subukan muli pagkalipas ng ilang minuto.",
    remove: "Alisin",
    removed: "Inalis ang domain",
    lastChecked: "Huling sinuri {{when}}",
    neverChecked: "Hindi pa nasusuri",
    sslActive: "Aktibo ang SSL",
    sslPending: "Hinihintay ang SSL",
    status: {
      pending: "Hinihintay ang DNS",
      verifying: "Bine-verify",
      active: "Na-verify",
      failed: "Nabigo ang pag-verify",
    },
    emptyTitle: "Wala pang custom domain",
    emptyBody: "Magdagdag para makipag-chat ang mga bisita sa sarili ninyong address sa halip na sa default na link.",
  },
  setup: {
    title: "Industriya at mga layunin",
    subtitle: "Hinuhubog nito ang mga mungkahing template, follow-up na tanong at ang profile ng organisasyon na alam ng assistant.",
    save: "I-save ang industriya at mga layunin",
    saved: "Na-save ang industriya at mga layunin",
    resuggest: "Magmungkahi ulit ng setup",
    resuggestTitle: "Magmungkahi ulit ng setup",
    resuggestBody: "Kumuha ng bagong mungkahing setup mula sa iyong industriya, mga layunin at paglalarawan. Walang magbabago hangga't hindi ka gumagawa ng bagong assistant.",
    target: "Imungkahi para sa",
    targetNew: "Bagong assistant",
    describe: "Ilarawan ang gusto mo (opsyonal)",
    suggest: "Magmungkahi",
    diffTitle: "Ano ang magbabago sa {{name}}",
    noDiff: "Tugma na ang assistant na ito sa mungkahi.",
    current: "Kasalukuyan",
    suggested: "Mungkahi",
    neverOverwrite: "Hindi kailanman ino-overwrite ang mga kasalukuyang assistant. Kopyahin ang gusto mo sa settings ng agent, o gumawa ng bagong assistant mula sa mungkahing ito.",
    createNew: "Gawin bilang bagong assistant",
    created: "Nagawa ang {{name}}",
    fields: {
      assistant_name: "Pangalan ng assistant",
      instructions: "Persona at saklaw",
      starter_questions: "Mga panimulang tanong",
      welcome_message: "Welcome message",
      audiences: "Audiences",
    },
    summaryStages: "Lead pipeline: {{stages}}",
    summaryStarters: "Mga panimulang tanong: {{starters}}",
  },
};
